import createClient, { type Middleware } from "openapi-fetch";
import { ApiError, networkError, toApiError } from "./errors";

export const TESTER_BASE_URL: string = import.meta.env.VITE_TESTER_API_URL ?? "http://localhost:7050/api/v1";
export const ADMIN_BASE_URL: string = import.meta.env.VITE_ADMIN_API_URL ?? "http://localhost:7060";

type TokenProvider = (forceRefresh?: boolean) => Promise<string | null>;
let tokenProvider: TokenProvider = async () => null;
const expiredListeners = new Set<() => void>();

export function setTokenProvider(fn: TokenProvider) {
  tokenProvider = fn;
}

export function onSessionExpired(cb: () => void): () => void {
  expiredListeners.add(cb);
  return () => expiredListeners.delete(cb);
}

async function withHeaders(req: Request, force: boolean): Promise<Request> {
  const headers = new Headers(req.headers);
  const token = await tokenProvider(force);
  if (token) headers.set("authorization", `Bearer ${token}`);
  else headers.delete("authorization");
  headers.set("x-request-id", crypto.randomUUID());
  return new Request(req, { headers });
}

export async function authFetch(
  input: Request,
  doFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> = globalThis.fetch.bind(globalThis),
): Promise<Response> {
  const retryCopy = input.clone();
  const first = await withHeaders(input, false);
  let res: Response;
  try {
    res = await doFetch(first);
  } catch (cause) {
    throw networkError(cause);
  }
  if (res.status !== 401) return res;
  if (!first.headers.has("authorization")) throw await toApiError(res);
  try {
    res = await doFetch(await withHeaders(retryCopy, true));
  } catch (cause) {
    throw networkError(cause);
  }
  if (res.status === 401) {
    for (const l of expiredListeners) l();
    throw await toApiError(res);
  }
  return res;
}

const throwOnError: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) throw await toApiError(response);
    return response;
  },
};

export type SharedHandler = (req: Request) => Promise<Response | null>;
let sharedHandler: SharedHandler | null = null;

// While a shared run is on screen, its run-scoped Tester API reads are answered from the cloud copy instead of the local runtime.
export function setSharedHandler(h: SharedHandler | null) {
  sharedHandler = h;
}

export function createApiClient<Paths extends object>(baseUrl: string) {
  const client = createClient<Paths>({
    baseUrl,
    fetch: async (req: Request) => (sharedHandler && baseUrl === TESTER_BASE_URL ? await sharedHandler(req) : null) ?? authFetch(req),
  });
  client.use(throwOnError);
  return client;
}

export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await p;
  if (error !== undefined && error !== null) {
    if (error instanceof ApiError) throw error;
    throw await toApiError(response);
  }
  return data as T;
}
